import { DateTime } from "luxon";

import LocalForagePlugin, { products, syncState } from "@/plugins/LocalForagePlugin";
import { productsSyncKey } from "@/types/products/product-definitions";

import { batchSetItems, batchRemoveItems } from "@/utils/localforage-batch";
import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";

import type { Products } from "@tmi/types/api/content-models";
import type { Product } from "@tmi/types/entities";

import { consoleLoggerFactory, getDefaultLoggerOptions, LogLevels } from "@/types/console-logger";
const logger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.DEBUG ) );

export async function refreshProductsFromServer(): Promise< Product[] > {
    let allProducts: Product[] = [];
    const response =
        await goFetch< Products, PersistedQueryPayload >( {
            endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
            payload: {
                id: 'b328c921a2e7a12d0894090087ef59b8',
                variables: { skip: 0, limit: 25 }
            }
        } );

    if ( response?.data?.productCatalog?.products ) {
        // cache only the products that are marked as "display-able"
        allProducts =
            [ ...( response.data.productCatalog.products
                    .sort( ( prod1: Product, prod2: Product ): number =>
                        ( prod1.name.toLowerCase() > prod2.name.toLowerCase() ) ? 1 :
                            ( ( prod1.name.toLowerCase() < prod2.name.toLowerCase() ) ? -1 : 0 ) )
            ) ];
    }

    return allProducts;
}

export async function loadProductMap( productsMap: Map< string, Product > ): Promise< boolean > {
    let updated = false;
    try {
        const t0: number = performance.now();

        if ( typeof products === 'undefined' || typeof syncState === 'undefined' ) {
            // Wait for the driver to settle
            await LocalForagePlugin.initialize();
        }

        await syncState.ready();
        // Check for the next refresh interval
        const syncThresholdInMillis = await syncState.getItem< number >( productsSyncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
            const productsReceived: Product[] = await refreshProductsFromServer();

            if(productsReceived.length > 0)
            {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { hours: 4 } ).toMillis();
                await syncState.setItem( productsSyncKey, nextSyncThresholdInMillis );
                updated = true;

                await products.ready();

                // Purge IDB entries that no longer exist on the server
                const receivedIds = new Set( productsReceived.map( p => p.id ) );
                const keysToRemove: string[] = [];
                await products.iterate( ( _: Product, key: string ) => {
                    if ( !receivedIds.has( key ) ) { keysToRemove.push( key ); }
                    return;
                } );
                await batchRemoveItems( products, keysToRemove );

                await batchSetItems( products, productsReceived.map( p => [ p.id, p ] as const ) );

                const t1: number = performance.now();
                logger.info( `[ sync-worker ] Refreshed ${ productsReceived.length } products from server in ${ t1 - t0 } ms (${ keysToRemove.length } stale removed)` );
            }
            else
            {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.setItem( productsSyncKey, nextSyncThresholdInMillis );
            }
        } else if ( productsMap.size > 0 ) {
            return updated;
        }
        // Make sure map is empty
        productsMap.clear();

        // Load whatever is in the local cache
        await products.ready();
        // Iterate over all elements within IDB
        await products.iterate( ( product: Product ) => {
            productsMap.set( product.id, product );
            return;
        } );

    } catch ( err: any ) {
        logger.error( '[ product-sync-coordinator ] Sync failed:', err );
    }

    return updated;
}